import { useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Bot,
  Cpu,
  Database,
  Gauge,
  RefreshCw,
  ShieldAlert,
  Server,
  TrendingUp,
  Wifi,
} from 'lucide-react';
import { format } from 'date-fns';
import { adminApi } from '@dukene/shared';
import { Area, AreaChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

type HistoryRange = 24 | 48 | 72 | 168;

interface ServerMetricCurrent {
  recordedAt: string;
  requestCount: number;
  avgResponseMs: number;
  p95ResponseMs: number;
  error4xx: number;
  error5xx: number;
  cacheHits: number;
  cacheMisses: number;
  cacheHitRatio: number;
  memoryUsedMb: number;
  cpuPercent: number;
  activeSseConnections: number;
  slowQueriesCount: number;
  statusCodes: Array<{ statusCode: number; count: number }>;
  topRoutes: Array<{ route: string; requestCount: number; avgResponseMs: number; error5xx: number }>;
  topErrorRoutes: Array<{ route: string; error5xx: number; requestCount: number }>;
}

interface ServerMetricHistoryRow {
  id: string;
  recordedAt: string;
  requestCount: number;
  avgResponseMs: number;
  p95ResponseMs: number;
  error4xx: number;
  error5xx: number;
  cacheHits: number;
  cacheMisses: number;
  cacheHitRatio: number;
  memoryUsedMb: number;
  cpuPercent: number;
  activeSseConnections: number;
  slowQueriesCount: number;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(value));
}

function formatDecimal(value: number, digits = 1): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

function formatPercent(value: number): string {
  return `${formatDecimal(value, 1)}%`;
}

function MetricCard({
  label,
  value,
  subtext,
  icon: Icon,
  tone = 'dark',
}: {
  label: string;
  value: string;
  subtext: string;
  icon: ComponentType<{ className?: string }>;
  tone?: 'dark' | 'soft';
}) {
  const toneClass = tone === 'dark'
    ? 'bg-black text-white border-black'
    : 'bg-white text-black border-gray-200';

  return (
    <div className={`relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-transform hover:-translate-y-1 ${toneClass}`}>
      <div className="absolute right-4 top-4 rounded-xl bg-white/10 p-3">
        <Icon className={`h-5 w-5 ${tone === 'dark' ? 'text-white' : 'text-black'}`} />
      </div>
      <p className={`text-[11px] font-black uppercase tracking-[0.24em] ${tone === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>{label}</p>
      <p className={`mt-10 text-3xl font-black tracking-tight ${tone === 'dark' ? 'text-white' : 'text-black'}`}>{value}</p>
      <p className={`mt-2 text-xs font-medium ${tone === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>{subtext}</p>
    </div>
  );
}

function SectionShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-black tracking-tight text-black">{title}</h2>
          <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

export default function ServerMetrics() {
  const [historyHours, setHistoryHours] = useState<HistoryRange>(24);

  const currentQuery = useQuery<ServerMetricCurrent>({
    queryKey: ['superadmin-server-metrics-current'],
    queryFn: () => adminApi.getServerMetricsCurrent(),
    refetchInterval: 30000,
  });

  const historyQuery = useQuery<ServerMetricHistoryRow[]>({
    queryKey: ['superadmin-server-metrics-history', historyHours],
    queryFn: () => adminApi.getServerMetricsHistory(historyHours),
    refetchInterval: 300000,
  });

  const current = currentQuery.data;
  const history = historyQuery.data || [];

  const chartData = useMemo(() => {
    return [...history]
      .slice()
      .reverse()
      .map((row) => ({
        time: format(new Date(row.recordedAt), 'HH:mm'),
        requests: row.requestCount,
        p95: row.p95ResponseMs,
        errors: row.error5xx,
        cache: row.cacheHitRatio,
        memory: row.memoryUsedMb,
        cpu: row.cpuPercent,
      }));
  }, [history]);

  const alerts = useMemo(() => {
    if (!current) return [];

    const items: Array<{ title: string; detail: string; tone: 'warn' | 'danger' | 'info' }> = [];

    if (current.error5xx > 0) {
      const route = current.topErrorRoutes?.[0];
      items.push({
        title: '5xx errors observed',
        detail: route ? `${route.error5xx} errors on ${route.route}` : `${current.error5xx} total 5xx responses`,
        tone: 'danger',
      });
    }

    if (current.slowQueriesCount > 0) {
      items.push({
        title: 'Slow queries detected',
        detail: `${current.slowQueriesCount} queries exceeded 500ms in the current window`,
        tone: 'warn',
      });
    }

    if (current.cacheHitRatio < 70) {
      items.push({
        title: 'Cache efficiency dipped',
        detail: `${formatPercent(current.cacheHitRatio)} hit ratio across the current window`,
        tone: 'warn',
      });
    }

    if (current.cpuPercent > 80) {
      items.push({
        title: 'CPU pressure',
        detail: `${formatDecimal(current.cpuPercent)}% average CPU across replicas`,
        tone: 'warn',
      });
    }

    if (current.memoryUsedMb > 700) {
      items.push({
        title: 'Memory pressure',
        detail: `${formatDecimal(current.memoryUsedMb, 0)} MB average RSS across replicas`,
        tone: 'warn',
      });
    }

    if (!items.length) {
      items.push({
        title: 'No active incidents',
        detail: 'The latest aggregated metrics do not show any critical conditions.',
        tone: 'info',
      });
    }

    return items;
  }, [current]);

  const lastUpdated = current?.recordedAt ? format(new Date(current.recordedAt), 'dd MMM yyyy, HH:mm') : 'Waiting for data';

  return (
    <div className="space-y-8">
      <div className="rounded-3xl border border-black bg-black px-8 py-7 text-white shadow-xl">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] font-black uppercase tracking-[0.28em] text-gray-300">Platform observability</p>
            <h1 className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">Server Metrics Command Center</h1>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-gray-300">
              Live request aggregation, hourly Postgres records, cache efficiency, slow-query pressure,
              replica resource usage, and incident-style logs in one place.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <select
              value={historyHours}
              onChange={(e) => setHistoryHours(Number(e.target.value) as HistoryRange)}
              className="h-12 rounded-2xl border border-white/15 bg-white/10 px-4 text-sm font-medium text-white outline-none backdrop-blur-md transition focus:border-white"
            >
              <option value={24} className="text-black">Last 24 hours</option>
              <option value={48} className="text-black">Last 48 hours</option>
              <option value={72} className="text-black">Last 72 hours</option>
              <option value={168} className="text-black">Last 7 days</option>
            </select>
            <button
              onClick={() => {
                void currentQuery.refetch();
                void historyQuery.refetch();
              }}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-white px-4 text-sm font-black text-black transition hover:scale-[1.01]"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Requests"
          value={current ? formatNumber(current.requestCount) : '—'}
          subtext="Current 60-minute window"
          icon={Activity}
          tone="dark"
        />
        <MetricCard
          label="Latency"
          value={current ? `${formatDecimal(current.avgResponseMs)} ms` : '—'}
          subtext={`p95 ${current ? `${formatDecimal(current.p95ResponseMs)} ms` : '—'}`}
          icon={Gauge}
          tone="soft"
        />
        <MetricCard
          label="Cache hit ratio"
          value={current ? formatPercent(current.cacheHitRatio) : '—'}
          subtext={`${current ? formatNumber(current.cacheHits) : '0'} hits / ${current ? formatNumber(current.cacheMisses) : '0'} misses`}
          icon={Server}
          tone="soft"
        />
        <MetricCard
          label="Replica load"
          value={current ? `${formatDecimal(current.cpuPercent)}% CPU` : '—'}
          subtext={`${current ? formatDecimal(current.memoryUsedMb, 0) : '0'} MB RSS, ${current ? formatNumber(current.activeSseConnections) : '0'} SSE clients`}
          icon={Cpu}
          tone="dark"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.45fr_0.95fr]">
        <SectionShell
          title="Operational chart"
          subtitle="Request volume, tail latency, cache efficiency, and resource pressure over time."
        >
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.24em] text-gray-500">Requests and latency</p>
                </div>
                <BarChart3 className="h-4 w-4 text-gray-500" />
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" stroke="#6b7280" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" stroke="#6b7280" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" stroke="#6b7280" tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ borderRadius: 16, border: '1px solid #e5e7eb' }}
                  />
                  <Line yAxisId="left" type="monotone" dataKey="requests" stroke="#000000" strokeWidth={2.5} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="p95" stroke="#6b7280" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.24em] text-gray-500">Cache, CPU, memory</p>
                </div>
                <TrendingUp className="h-4 w-4 text-gray-500" />
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="fillMemory" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#000000" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#000000" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" stroke="#6b7280" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#6b7280" tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 16, border: '1px solid #e5e7eb' }} />
                  <Area type="monotone" dataKey="cache" stroke="#111827" fillOpacity={1} fill="url(#fillMemory)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </SectionShell>

        <SectionShell
          title="Current status"
          subtitle="SSE, error health, and the latest operational notes."
        >
          <div className="space-y-4">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-black p-2 text-white">
                  <Server className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-black text-black">Last flushed record</p>
                  <p className="mt-1 text-sm text-gray-600">{lastUpdated}</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-black p-2 text-white">
                  <Wifi className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-black text-black">SSE connections</p>
                  <p className="mt-1 text-sm text-gray-600">
                    {current ? formatNumber(current.activeSseConnections) : '0'} active dashboard streams
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-black p-2 text-white">
                  <Bot className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-black text-black">Replica coverage</p>
                  <p className="mt-1 text-sm text-gray-600">
                    Current aggregates come from Redis snapshots across active API replicas.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-black p-2 text-white">
                  <Database className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-black text-black">Postgres record</p>
                  <p className="mt-1 text-sm text-gray-600">
                    {history.length} hourly rows available in the current history window.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </SectionShell>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <SectionShell
          title="Logs and alerts"
          subtitle="Operational signals derived from current metrics, including error spikes and slow queries."
        >
          <div className="space-y-3">
            {alerts.map((alert) => (
              <div
                key={`${alert.title}-${alert.detail}`}
                className={`rounded-2xl border p-4 ${alert.tone === 'danger'
                  ? 'border-red-200 bg-red-50'
                  : alert.tone === 'warn'
                    ? 'border-amber-200 bg-amber-50'
                    : 'border-gray-200 bg-gray-50'
                  }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`rounded-xl p-2 ${alert.tone === 'danger' ? 'bg-red-600 text-white' : alert.tone === 'warn' ? 'bg-amber-500 text-white' : 'bg-black text-white'}`}>
                    {alert.tone === 'danger' ? <ShieldAlert className="h-4 w-4" /> : alert.tone === 'warn' ? <AlertTriangle className="h-4 w-4" /> : <Activity className="h-4 w-4" />}
                  </div>
                  <div>
                    <p className="text-sm font-black text-black">{alert.title}</p>
                    <p className="mt-1 text-sm text-gray-600">{alert.detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SectionShell>

        <SectionShell
          title="Status code breakdown"
          subtitle="Raw response family counts for the active 60-minute window."
        >
          <div className="space-y-3">
            {current?.statusCodes?.length ? current.statusCodes.map((row) => (
              <div key={row.statusCode} className="flex items-center justify-between rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
                <div>
                  <p className="text-sm font-black text-black">HTTP {row.statusCode}</p>
                  <p className="text-xs text-gray-500">Current window</p>
                </div>
                <p className="text-sm font-black text-black">{formatNumber(row.count)}</p>
              </div>
            )) : (
              <p className="text-sm text-gray-500">No status code data yet.</p>
            )}
          </div>
        </SectionShell>

        <SectionShell
          title="Top routes"
          subtitle="Most active routes and the noisiest failure sources."
        >
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-xs font-black uppercase tracking-[0.24em] text-gray-500">Most requested</p>
              <div className="space-y-2">
                {current?.topRoutes?.length ? current.topRoutes.slice(0, 5).map((row) => (
                  <div key={row.route} className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-black text-black">{row.route}</p>
                        <p className="text-xs text-gray-500">Avg {formatDecimal(row.avgResponseMs)} ms</p>
                      </div>
                      <p className="text-sm font-black text-black">{formatNumber(row.requestCount)}</p>
                    </div>
                  </div>
                )) : <p className="text-sm text-gray-500">No route data yet.</p>}
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-black uppercase tracking-[0.24em] text-gray-500">Error hot spots</p>
              <div className="space-y-2">
                {current?.topErrorRoutes?.length ? current.topErrorRoutes.slice(0, 5).map((row) => (
                  <div key={row.route} className="rounded-2xl border border-red-200 bg-red-50 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-black text-black">{row.route}</p>
                        <p className="text-xs text-gray-500">Failure source</p>
                      </div>
                      <p className="text-sm font-black text-black">{formatNumber(row.error5xx)} x 500</p>
                    </div>
                  </div>
                )) : <p className="text-sm text-gray-500">No 5xx routes detected.</p>}
              </div>
            </div>
          </div>
        </SectionShell>
      </div>

      <SectionShell
        title="Hourly records"
        subtitle="Each row is the persisted Postgres snapshot generated by the hourly flush job."
      >
        <div className="overflow-hidden rounded-2xl border border-gray-200">
          <div className="max-h-[560px] overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="sticky top-0 bg-gray-50 text-xs font-black uppercase tracking-[0.24em] text-gray-500">
                <tr>
                  <th className="px-4 py-3">Recorded</th>
                  <th className="px-4 py-3">Requests</th>
                  <th className="px-4 py-3">Avg</th>
                  <th className="px-4 py-3">p95</th>
                  <th className="px-4 py-3">Errors</th>
                  <th className="px-4 py-3">Cache</th>
                  <th className="px-4 py-3">Memory</th>
                  <th className="px-4 py-3">CPU</th>
                  <th className="px-4 py-3">SSE</th>
                  <th className="px-4 py-3">Slow</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {historyQuery.isLoading ? (
                  <tr>
                    <td className="px-4 py-10 text-gray-500" colSpan={10}>Loading records...</td>
                  </tr>
                ) : history.length ? history.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-xs font-medium text-gray-500">
                      {format(new Date(row.recordedAt), 'dd MMM HH:mm')}
                    </td>
                    <td className="px-4 py-3 font-black text-black">{formatNumber(row.requestCount)}</td>
                    <td className="px-4 py-3">{formatDecimal(row.avgResponseMs)} ms</td>
                    <td className="px-4 py-3">{formatDecimal(row.p95ResponseMs)} ms</td>
                    <td className="px-4 py-3">{formatNumber(row.error4xx + row.error5xx)}</td>
                    <td className="px-4 py-3">{formatPercent(row.cacheHitRatio)}</td>
                    <td className="px-4 py-3">{formatDecimal(row.memoryUsedMb, 0)} MB</td>
                    <td className="px-4 py-3">{formatDecimal(row.cpuPercent)}%</td>
                    <td className="px-4 py-3">{formatNumber(row.activeSseConnections)}</td>
                    <td className="px-4 py-3">{formatNumber(row.slowQueriesCount)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td className="px-4 py-10 text-gray-500" colSpan={10}>No hourly records yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </SectionShell>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <SectionShell
          title="Replica snapshots"
          subtitle="Cross-replica memory and CPU details sampled from Redis by the hourly job."
        >
          <div className="space-y-3 text-sm text-gray-700">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-black uppercase tracking-[0.24em] text-gray-500">Average CPU</p>
              <p className="mt-2 text-2xl font-black text-black">{current ? `${formatDecimal(current.cpuPercent)}%` : '—'}</p>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-black uppercase tracking-[0.24em] text-gray-500">Average memory</p>
              <p className="mt-2 text-2xl font-black text-black">{current ? `${formatDecimal(current.memoryUsedMb, 0)} MB` : '—'}</p>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-black uppercase tracking-[0.24em] text-gray-500">Live SSE clients</p>
              <p className="mt-2 text-2xl font-black text-black">{current ? formatNumber(current.activeSseConnections) : '—'}</p>
            </div>
          </div>
        </SectionShell>

        <SectionShell
          title="Raw details"
          subtitle="The active payload exactly as the backend returns it, useful for support and audits."
        >
          <pre className="max-h-[420px] overflow-auto rounded-2xl bg-black p-4 text-xs leading-6 text-white">
{JSON.stringify(current ?? {}, null, 2)}
          </pre>
        </SectionShell>
      </div>
    </div>
  );
}
